/**
 * Email domain validation utilities
 *
 * This module provides validation for email domains to prevent spam submissions
 * and enforce quality standards for project descriptions based on email provider type.
 *
 * Business Logic:
 * - Free email providers (Gmail, Yahoo, etc.) require longer project descriptions (100+ chars)
 *   to filter out low-effort spam submissions
 * - Business/organization emails require shorter descriptions (20+ chars)
 * - Certain domains can be completely blocked from submission
 */

/**
 * Domains that should be completely blocked (silent fail)
 * Can be extended via environment variable BLOCKED_EMAIL_DOMAINS (comma-separated)
 */
export const BLOCKED_DOMAINS: readonly string[] = [
    // Add domains to block here
    // Example: 'spam-domain.com', 'blocked-email.com'
] as const;

/**
 * Common free email providers that require stricter validation
 * These providers are often used for spam or low-quality submissions
 */
export const COMMON_EMAIL_DOMAINS: readonly string[] = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'aol.com',
    'icloud.com',
    'mail.com',
    'protonmail.com',
    'zoho.com',
    'gmx.com',
    'yandex.com',
    'live.com',
    'msn.com',
] as const;

/**
 * Minimum character requirements for project descriptions
 *
 * - DEFAULT (20 chars): For business/organization emails
 * - COMMON_DOMAINS (100 chars): For free email providers to ensure quality submissions
 *
 * These can be overridden via environment variables:
 * - MIN_PROJECT_DESCRIPTION_LENGTH_DEFAULT
 * - MIN_PROJECT_DESCRIPTION_LENGTH_COMMON_DOMAINS
 */
export const MIN_PROJECT_DESCRIPTION_LENGTH_DEFAULT = parseInt(
    process.env['MIN_PROJECT_DESCRIPTION_LENGTH_DEFAULT'] || '20',
    10
);

export const MIN_PROJECT_DESCRIPTION_LENGTH_COMMON_DOMAINS = parseInt(
    process.env['MIN_PROJECT_DESCRIPTION_LENGTH_COMMON_DOMAINS'] || '100',
    10
);

/**
 * Extract domain from email address with robust validation
 *
 * @param email - Email address to extract domain from
 * @returns Domain in lowercase, or empty string if invalid
 *
 * @example
 * extractDomain('user@example.com') // 'example.com'
 * extractDomain('CAPS@EXAMPLE.COM') // 'example.com'
 * extractDomain('  user@example.com  ') // 'example.com'
 * extractDomain('invalid') // ''
 * extractDomain('multiple@@signs.com') // ''
 */
export const extractDomain = (email: string): string => {
    if (!email || typeof email !== 'string') {
        return '';
    }

    const trimmedEmail = email.trim().toLowerCase();
    const parts = trimmedEmail.split('@');

    // Valid email should have exactly 2 parts (local@domain)
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
        return '';
    }

    return parts[1];
};

/**
 * Get list of blocked domains from environment or default list
 * Combines hardcoded list with comma-separated BLOCKED_EMAIL_DOMAINS env var
 *
 * @returns Array of blocked domains in lowercase
 */
export const getBlockedDomains = (): readonly string[] => {
    const envDomains = process.env['BLOCKED_EMAIL_DOMAINS'] || '';
    const additionalDomains = envDomains
        .split(',')
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean);

    return [...BLOCKED_DOMAINS, ...additionalDomains];
};

/**
 * Check if email domain is blocked
 *
 * @param email - Email address to check
 * @returns True if domain is in blocked list
 *
 * @example
 * isBlockedDomain('user@blocked-domain.com') // true if in BLOCKED_DOMAINS
 * isBlockedDomain('user@legitimate.com') // false
 */
export const isBlockedDomain = (email: string): boolean => {
    const domain = extractDomain(email);
    if (!domain) {
        return false;
    }

    const blockedDomains = getBlockedDomains();
    return blockedDomains.includes(domain);
};

/**
 * Check if email domain is a common free provider
 *
 * @param email - Email address to check
 * @returns True if domain is a common free email provider
 *
 * @example
 * isCommonDomain('user@gmail.com') // true
 * isCommonDomain('user@company.com') // false
 */
export const isCommonDomain = (email: string): boolean => {
    const domain = extractDomain(email);
    if (!domain) {
        return false;
    }

    return COMMON_EMAIL_DOMAINS.includes(domain);
};

/**
 * Get minimum project description length based on email domain
 *
 * Free email providers require longer descriptions to filter spam.
 * Organization emails have lower requirements.
 *
 * @param email - Email address to check
 * @returns Minimum required character length
 *
 * @example
 * getMinProjectDescriptionLength('user@gmail.com') // 100
 * getMinProjectDescriptionLength('user@company.com') // 20
 */
export const getMinProjectDescriptionLength = (email: string): number => {
    return isCommonDomain(email)
        ? MIN_PROJECT_DESCRIPTION_LENGTH_COMMON_DOMAINS
        : MIN_PROJECT_DESCRIPTION_LENGTH_DEFAULT;
};

/**
 * Validate email format
 * Basic validation to ensure email has correct structure
 *
 * @param email - Email address to validate
 * @returns True if email has valid format
 */
export const isValidEmailFormat = (email: string): boolean => {
    if (!email || typeof email !== 'string') {
        return false;
    }

    const domain = extractDomain(email);
    return domain.length > 0 && domain.includes('.');
};
